import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { optImg } from "../../lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";

const inputCls = "w-full rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:border-pine focus:ring-2 focus:ring-pine/15";
const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500";

const EMPTY = {
  name: "", slug: "", category: "Highlights", short_description: "", description: "",
  image_url: "", duration: "", meeting_point: "", highlightsText: "", galleryText: "",
  itineraryText: "", featured: false, order: 0,
};

const toForm = (t) => ({
  ...EMPTY, ...t,
  highlightsText: (t.highlights || []).join("\n"),
  galleryText: (t.gallery || []).join("\n"),
  itineraryText: (t.itinerary || []).map((s) => `${s.time} | ${s.title} | ${s.description}`).join("\n"),
});

const toPayload = (f) => ({
  name: f.name, slug: f.slug || null, category: f.category,
  short_description: f.short_description, description: f.description,
  image_url: f.image_url, duration: f.duration, meeting_point: f.meeting_point,
  highlights: f.highlightsText.split("\n").map((s) => s.trim()).filter(Boolean),
  gallery: f.galleryText.split("\n").map((s) => s.trim()).filter(Boolean),
  itinerary: f.itineraryText.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((p) => p[1])
    .map((p) => ({ time: p[0] || "", title: p[1], description: p[2] || "" })),
  featured: f.featured, order: Number(f.order) || 0,
});

const AdminTours = ({ onSaved }) => {
  const [tours, setTours] = useState(null);
  const [editing, setEditing] = useState(null); // null | form object
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tours");
      setTours(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(EMPTY); setEditingId(null); };
  const openEdit = (t) => { setEditing(toForm(t)); setEditingId(t.id); };

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Tour name is required"); return; }
    setSaving(true);
    try {
      const payload = toPayload(editing);
      if (editingId) await api.put(`/admin/tours/${editingId}`, payload);
      else await api.post("/admin/tours", payload);
      toast.success("Tour saved");
      setEditing(null);
      load(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}" permanently?`)) return;
    try {
      await api.delete(`/admin/tours/${t.id}`);
      toast.success("Tour deleted");
      load(); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const set = (k, v) => setEditing((f) => ({ ...f, [k]: v }));

  if (!tours) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-pine" /></div>;

  return (
    <div data-testid="admin-tours">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">{tours.length} tours on the website</p>
        <button onClick={openNew} data-testid="add-tour-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-xs font-semibold text-white hover:bg-pine-light">
          <Plus className="h-3.5 w-3.5" /> Add tour
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {tours.map((t) => (
          <div key={t.id} className="flex gap-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-soft" data-testid={`admin-tour-${t.slug}`}>
            <img src={optImg(t.image_url, 300)} alt={t.name} loading="lazy" decoding="async" className="h-20 w-24 shrink-0 rounded-xl object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-semibold text-pine">{t.name}</p>
                {t.featured && <Star className="h-3.5 w-3.5 fill-gold text-gold" />}
              </div>
              <p className="text-xs text-gray-400">{t.category} · {t.duration || "no duration"}</p>
              <div className="mt-2 flex gap-2">
                <button onClick={() => openEdit(t)} data-testid={`edit-tour-${t.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                  <Pencil className="h-3 w-3" /> Edit
                </button>
                <button onClick={() => remove(t)} data-testid={`delete-tour-${t.slug}`}
                        className="inline-flex items-center gap-1 rounded-full border border-red-100 px-3 py-1 text-xs font-semibold text-red-400 hover:bg-red-50">
                  <Trash2 className="h-3 w-3" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="tour-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-pine">{editingId ? "Edit tour" : "New tour"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Name *</label>
                <input className={inputCls} value={editing.name} onChange={(e) => set("name", e.target.value)} data-testid="tour-form-name" />
              </div>
              <div>
                <label className={labelCls}>URL slug (auto if empty)</label>
                <input className={inputCls} value={editing.slug} onChange={(e) => set("slug", e.target.value)} data-testid="tour-form-slug" placeholder="my-tour-name" />
              </div>
              <div>
                <label className={labelCls}>Category</label>
                <input className={inputCls} value={editing.category} onChange={(e) => set("category", e.target.value)} data-testid="tour-form-category" />
              </div>
              <div>
                <label className={labelCls}>Duration</label>
                <input className={inputCls} value={editing.duration} onChange={(e) => set("duration", e.target.value)} data-testid="tour-form-duration" placeholder="approx. 5 hours" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Short description (card)</label>
                <input className={inputCls} value={editing.short_description} onChange={(e) => set("short_description", e.target.value)} data-testid="tour-form-short" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Full description</label>
                <textarea rows={5} className={inputCls} value={editing.description} onChange={(e) => set("description", e.target.value)} data-testid="tour-form-description" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Main image URL</label>
                <input className={inputCls} value={editing.image_url} onChange={(e) => set("image_url", e.target.value)} data-testid="tour-form-image" />
                {editing.image_url && <img src={optImg(editing.image_url, 400)} alt="Preview" className="mt-2 h-28 w-44 rounded-lg object-cover" />}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Meeting point info</label>
                <input className={inputCls} value={editing.meeting_point} onChange={(e) => set("meeting_point", e.target.value)} data-testid="tour-form-meeting" />
              </div>
              <div>
                <label className={labelCls}>Highlights (one per line)</label>
                <textarea rows={4} className={inputCls} value={editing.highlightsText} onChange={(e) => set("highlightsText", e.target.value)} data-testid="tour-form-highlights" />
              </div>
              <div>
                <label className={labelCls}>Itinerary (time | title | description)</label>
                <textarea rows={4} className={inputCls} value={editing.itineraryText} onChange={(e) => set("itineraryText", e.target.value)} data-testid="tour-form-itinerary" placeholder="09:00 | Pickup | From your hotel" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Extra gallery image URLs (one per line)</label>
                <textarea rows={2} className={inputCls} value={editing.galleryText} onChange={(e) => set("galleryText", e.target.value)} data-testid="tour-form-gallery" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <Switch checked={editing.featured} onCheckedChange={(v) => set("featured", v)} data-testid="tour-form-featured" /> Featured on homepage
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Order</label>
                  <input type="number" className={`${inputCls} w-20`} value={editing.order} onChange={(e) => set("order", e.target.value)} data-testid="tour-form-order" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <button onClick={save} disabled={saving} data-testid="tour-form-save"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pine px-6 py-3 text-sm font-semibold text-white hover:bg-pine-light disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save tour
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTours;
